import { Calculation } from "@/services/backend/qpm/calculations/types/calculation";
import { createContext, ReactNode, useContext, useMemo } from "react";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { useQpmCalculationShowQuery } from "@/services/backend/qpm/calculations/api/calculation-api";

export type CalculationDataContextType = {
  calculation: Calculation | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
};

const CalculationDataContext = createContext<
  CalculationDataContextType | undefined
>(undefined);

export function CalculationDataProvider({
  calculationId,
  children,
}: {
  calculationId: string;
  children: ReactNode;
}) {
  const { data, isLoading, isError, error, refetch } =
    useQpmCalculationShowQuery({ id: calculationId });

  const value = useMemo<CalculationDataContextType>(
    () => ({
      calculation: data,
      isLoading,
      isError,
      error,
      refetch,
    }),
    [data, isLoading, isError, error, refetch],
  );

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  return (
    <CalculationDataContext.Provider value={value}>
      {children}
    </CalculationDataContext.Provider>
  );
}

export const useCalculationData = () => {
  const context = useContext(CalculationDataContext);
  if (!context)
    throw new Error(
      "useCalculationData must be used within CalculationDataProvider",
    );
  return context;
};
